'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { TableNoFilterResults } from '@morpho-org/ui-v6/client';
import type { DashboardEarnPositionsFilters } from '@/helpers/filter/table';
import type { UserEarnPositionFacets } from '@/hooks/positions/userEarnPositionsQuery';
import { MobileVaultPositionCard } from '@/components/common/MobileEarnCards';
import { UpgradeButton, useV1ToV2MigrationTarget } from '@/components/common/V1ToV2Migration';
import { getVaultUrl, VaultPageTabValues } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { EarnPositionsTableFeatures } from './config/DashboardEarnPositionsTable.columns';
import type { DashboardEarnPositionsTableRow } from './config/DashboardEarnPositionsTable.types';
import DashboardEarnPositionsFiltersComponent from './DashboardEarnPositionsFilters';
interface DashboardEarnPositionsMobileViewProps {
  table: ReactTable<EarnPositionsTableFeatures, DashboardEarnPositionsTableRow>;
  filters?: DashboardEarnPositionsFilters;
  onFiltersChange?: (filters: DashboardEarnPositionsFilters) => void;
  positionChainIds?: ChainId[];
  totalPositionsCount: number;
  loading: boolean;
  disabled?: boolean;
  facets?: UserEarnPositionFacets;
}
const DashboardEarnPositionsMobileView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  loading,
  disabled,
  totalPositionsCount,
  facets
}: DashboardEarnPositionsMobileViewProps) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const hasFilteredResults = table.getRowModel().rows.length > 0;
  return <div data-testid="dashboard-earn-positions-list" className="gap-sm flex flex-col">
      <DashboardEarnPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
      {!hasFilteredResults ? <TableNoFilterResults withBorder type="vaults" count={totalPositionsCount} clearFilters={() => onFiltersChange?.({
      assetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <>
          {table.getRowModel().rows.map(row => <DashboardMobileVaultRow key={row.id} row={row.original} isSelf={isSelf} />)}
        </>}
    </div>;
};
const DashboardMobileVaultRow = t0 => {
  const $ = _c(38);
  const {
    row,
    isSelf
  } = t0;
  const {
    vault,
    assets,
    isVaultV2,
    collateralExposures,
    netApy
  } = row;
  const chainId = vault.asset.chain.id;
  const migrationTarget = useV1ToV2MigrationTarget(isVaultV2 || !isSelf ? undefined : vault.address, chainId);
  if (!assets.assets) {
    return null;
  }
  let t1;
  if ($[0] !== chainId || $[1] !== isSelf || $[2] !== vault.address || $[3] !== vault.name) {
    t1 = getVaultUrl({
      identifier: InterfaceChainUtils.getIdentifierById(chainId),
      vaultAddress: vault.address,
      vaultName: vault.name,
      mobileTab: isSelf ? VaultPageTabValues.MyPosition : undefined
    });
    $[0] = chainId;
    $[1] = isSelf;
    $[2] = vault.address;
    $[3] = vault.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const vaultUrl = t1;
  let t2;
  if ($[5] !== isVaultV2 || $[6] !== vault.address || $[7] !== vault.asset || $[8] !== vault.name || $[9] !== vault.totalAssetsUsd || $[10] !== vault.warnings) {
    t2 = {
      address: vault.address,
      name: vault.name,
      asset: vault.asset,
      totalAssetsUsd: vault.totalAssetsUsd,
      isVaultV2,
      warnings: vault.warnings
    };
    $[5] = isVaultV2;
    $[6] = vault.address;
    $[7] = vault.asset;
    $[8] = vault.name;
    $[9] = vault.totalAssetsUsd;
    $[10] = vault.warnings;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== netApy.apy || $[13] !== netApy.apyBreakdown || $[14] !== netApy.includesDonations || $[15] !== netApy.isRateCapped || $[16] !== netApy.managementFee || $[17] !== netApy.maxApy || $[18] !== netApy.maxRate || $[19] !== netApy.netApy || $[20] !== netApy.performanceFee || $[21] !== netApy.points || $[22] !== netApy.rewards) {
    t3 = {
      native: netApy.apy,
      net: netApy.netApy,
      rewards: netApy.rewards,
      performanceFee: netApy.performanceFee,
      managementFee: netApy.managementFee,
      maxRate: netApy.maxRate,
      maxApy: netApy.maxApy,
      isRateCapped: netApy.isRateCapped,
      includesDonations: netApy.includesDonations,
      apyBreakdown: netApy.apyBreakdown,
      points: netApy.points
    };
    $[12] = netApy.apy;
    $[13] = netApy.apyBreakdown;
    $[14] = netApy.includesDonations;
    $[15] = netApy.isRateCapped;
    $[16] = netApy.managementFee;
    $[17] = netApy.maxApy;
    $[18] = netApy.maxRate;
    $[19] = netApy.netApy;
    $[20] = netApy.performanceFee;
    $[21] = netApy.points;
    $[22] = netApy.rewards;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== assets.assets || $[25] !== assets.assetsUsd) {
    t4 = {
      amount: assets.assets,
      usdValue: assets.assetsUsd
    };
    $[24] = assets.assets;
    $[25] = assets.assetsUsd;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== migrationTarget) {
    t5 = migrationTarget ? <UpgradeButton migrationTarget={migrationTarget} fullWidth={true} /> : undefined;
    $[27] = migrationTarget;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== collateralExposures.collateralExposures || $[30] !== isSelf || $[31] !== t2 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== vault.curators || $[36] !== vaultUrl) {
    t6 = <MobileVaultPositionCard href={vaultUrl} vault={t2} apy={t3} position={t4} curators={vault.curators} collateralExposures={collateralExposures.collateralExposures} displayNetwork={true} isSelf={isSelf} primaryCta={t5} />;
    $[29] = collateralExposures.collateralExposures;
    $[30] = isSelf;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = vault.curators;
    $[36] = vaultUrl;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  return t6;
};
export default DashboardEarnPositionsMobileView;